import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTheme } from 'react-jss';
import { useNavigate } from 'react-router';
import { Box, Button } from '@mui/material';
import backgroundImg from '../../assets/backgrounds/img.jpg';
import dugoodImg from '../../assets/icons/dugood.svg';
import { BackBtn } from '../../components/backBtn/BackBtn';
import { VerificationForm } from '../../components/forms';
import { IVerifyFormValues } from '../../components/forms/verificationForm/types';
import { SubmitBtn } from '../../components/submitBtn/SubmitBtn';
import { ITheme } from '../../components/theme';
import { useAlert } from '../../hooks/useAlert';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { resetPassword, verifyCode } from '../../store/actions/actions';
import { routesNames } from '../../utils/constants';
import { timer } from '../../utils/timer';
import { useStyles } from './verifyCode.styles';

export const VerifyCode = (): JSX.Element => {
  const { register, handleSubmit, formState: { errors, isDirty } } = useForm<IVerifyFormValues>({
    defaultValues: {
      verificationCode: '',
    },
  });
  const { setAlert } = useAlert();
  const email = useAppSelector((state) => state.organizerReducer?.resetPasswordEmail);
  const [showResendCode, setShowResendCode] = useState<boolean>(false);
  const [seconds, setSeconds] = useState<number>(60);
  const formRef = useRef<HTMLFormElement>(null);
  const theme: ITheme | undefined = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const classes = useStyles({ theme });
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  useEffect(() => {
    const intervalId = timer(seconds, setSeconds);
    if (seconds === 0) {
      clearInterval(intervalId);
      setShowResendCode(true);
    }

    return () => clearInterval(intervalId);
  }, [seconds]);

  useEffect(() => {
    if (!email) {
      navigate(routesNames.logIn);
    }
  }, [email, navigate]);

  const handleSubmitResend = useCallback((): void => {
    resetPassword({ email })(dispatch).then(() => {
      setSeconds(60);
      setShowResendCode(false);
    });
  }, []);

  const onSubmit: SubmitHandler<IVerifyFormValues> = useCallback((data): void => {
    const { verificationCode } = data;
    verifyCode({ verificationCode, email })(dispatch)
      .then(() => {
        navigate(routesNames.createPassword);
      }).catch((error) => {
        let message: string = '';
        if (error instanceof Error) {
          message = error.message;
        }
        else {
          message = String(error);
        }
        setAlert(message, 'error');
      });
    formRef?.current?.reset();
  }, []);

  return (
    <Box className={classes.mainBlock}>
      <div className={classes.img}>
        <img src={dugoodImg} alt="" className={classes.img2} />
        <img src={backgroundImg} alt="" className={classes.img1} />
      </div>
      <Box
        ref={formRef}
        component="form"
        className={classes.form}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div>
          <h1 className={classes.mainTitle}>Verification code</h1>
        </div>
        <div className={classes.check}>
          <span className={classes.text}>
            A verification code has been sent to <span className={classes.emailText}>{email}</span>
          </span>
        </div>
        <VerificationForm
          required
          errors={errors}
          register={register}
        />
        {
          !showResendCode ? (
            <div className={classes.resend}>
              <span className={classes.text}
              >Resend code in
              </span>
              <span className={classes.time}
              > {minutes}:{remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds}
              </span>
            </div>
          ) : (<div></div>)
        }
        {
          showResendCode ? (
            <Button
              className={classes.button}
              variant="contained"
              onClick={handleSubmitResend}
            >
              Resend Code
            </Button>
          ) : (<div></div>)
        }
        <SubmitBtn text={'Enter'} disabled={!isDirty || Object.keys(errors).length > 0} />
        <BackBtn text={'Back to Log in'} />
      </Box>
    </Box>
  );
};
